// src/components/PopularLinksTab.js
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// Matches the “blog post card” style from BlogPost.js
const containerStyle = (isDesktop) => ({
  width: "100%",
  maxWidth: isDesktop ? "840px" : "100%",
  margin: isDesktop ? "0 auto 2rem" : "0 auto 1.5rem",
  backgroundColor: isDesktop ? "#fff" : "transparent",
  borderRadius: isDesktop ? "15px" : "0",
  boxShadow: isDesktop ? "0 4px 24px rgba(0, 0, 0, 0.1)" : "none",
  padding: isDesktop ? "1.25rem" : "0.75rem",
  boxSizing: "border-box",
});

const titleStyle = (isDesktop) => ({
  fontSize: isDesktop ? "1.5rem" : "1.25rem",
  fontWeight: "bold",
  marginBottom: isDesktop ? "1rem" : "0.75rem",
  textAlign: isDesktop ? "center" : "left",
  lineHeight: "1.2",
  color: "#1a1a1a",
  letterSpacing: isDesktop ? "-0.02em" : "normal",
});

const linkItemStyle = (isDesktop) => ({
  backgroundColor: isDesktop ? "#f8f9fa" : "transparent",
  borderRadius: isDesktop ? "12px" : "0",
  padding: isDesktop ? "1rem" : "0.5rem 0.25rem",
  border: isDesktop ? "1px solid #eaeaea" : "none",
  boxShadow: isDesktop ? "0 4px 12px rgba(0, 0, 0, 0.05)" : "none",
  margin: isDesktop ? "1rem auto" : "0.75rem auto",
  maxWidth: isDesktop ? "750px" : "100%",
  transition: "all 0.3s ease",
});

export default function PopularLinksTab({ isDesktop }) {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    async function fetchLinks() {
      try {
        const response = await fetch("/api/popular-links");
        if (!response.ok) {
          throw new Error("Failed to fetch popular links");
        }
        const data = await response.json();
        setLinks(data);
      } catch (error) {
        console.error("Error fetching popular links:", error);
      }
    }
    fetchLinks();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      style={containerStyle(isDesktop)}
    >
      {/* Main Title */}
      <h1 style={titleStyle(isDesktop)}>Popular Links</h1>

      {/* Subheading: Date Range */}
      <h3
        style={{
          textAlign: isDesktop ? "center" : "left",
          color: "#555",
          marginTop: isDesktop ? "-0.5rem" : "-0.5rem",
          marginBottom: isDesktop ? "1.25rem" : "1rem",
          fontSize: isDesktop ? "0.9rem" : "0.85rem",
        }}
      >
        January 2024 – January 2025
      </h3>

      {/* If no data yet, show a loading/fallback */}
      {links.length === 0 ? (
        <p style={{ textAlign: "center" }}>Loading links…</p>
      ) : (
        <>
          {links.map((item, index) => (
            <LinkItem key={index} item={item} index={index} isDesktop={isDesktop} />
          ))}

        </>
      )}
    </motion.div>
  );
}

function LinkItem({ item, index, isDesktop }) {
  // If your API has item.URL instead of item.url:
  const { title, URL, description, dateAdded } = item;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.1 * index }}
      whileHover={isDesktop ? { scale: 1.02 } : {}}
      style={linkItemStyle(isDesktop)}
    >
      {/* Title row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: isDesktop ? "1.1rem" : "1rem",
            fontWeight: "bold",
            color: "#0066cc",
            lineHeight: 1.3,
          }}
        >
          <a
            href={URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0066cc", textDecoration: "none" }}
          >
            {title}
          </a>
        </h2>

        {/* Show dateAdded if present */}
        {dateAdded && isDesktop && (
          <span
            style={{
              fontSize: "0.85rem",
              color: "#666",
              backgroundColor: "#eaeaea",
              padding: "0.25rem 0.5rem",
              borderRadius: "4px",
            }}
          >
            Added: {dateAdded}
          </span>
        )}
      </div>

      {/* Description */}
      {description && (
        <p
          style={{
            fontSize: isDesktop ? "0.95rem" : "0.9rem",
            color: "#444",
            marginTop: "0.5rem",
            lineHeight: 1.6,
          }}
        >
          {description}
        </p>
      )}

      {/* Domain & Button row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "0.75rem",
        }}
      >
        <span style={{ fontSize: "0.85rem", color: "#666" }}>
          {URL ? URL.replace(/(^\w+:|^)\/\//, "").split("/")[0] : "N/A"}
        </span>

        <motion.a
          whileHover={isDesktop ? { scale: 1.05 } : {}}
          href={URL || "#"}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            backgroundColor: "#0066cc",
            color: "white",
            border: "none",
            borderRadius: "6px",
            padding: "0.5rem 1rem",
            fontSize: "0.85rem",
            fontWeight: 600,
            textDecoration: "none",
            cursor: URL ? "pointer" : "default",
          }}
          onClick={(e) => {
            if (!URL) e.preventDefault();
          }}
        >
          Visit
        </motion.a>
      </div>
    </motion.div>
  );
}
